import { useCallback, useEffect, useState } from 'react';
import { SIDEBAR_MAX_VW, SIDEBAR_MIN_WIDTH, useOptibotStore } from '../stores/optibot-store';

/** Clamp a desired width to [min, max], where max is a fraction of the viewport. */
export function clampSidebarWidth(width: number): number {
  const max = Math.max(SIDEBAR_MIN_WIDTH, Math.round(window.innerWidth * SIDEBAR_MAX_VW));
  return Math.min(Math.max(width, SIDEBAR_MIN_WIDTH), max);
}

/**
 * Drag-to-resize behavior for the docked OptiBot sidebar. The panel is anchored
 * to the right edge, so the dragged width is the distance from the pointer to
 * the right edge of the viewport. Width is persisted in the optibot store.
 */
export function useResizableSidebar() {
  const sidebarWidth = useOptibotStore((s) => s.sidebarWidth);
  const setSidebarWidth = useOptibotStore((s) => s.setSidebarWidth);
  const [isResizing, setIsResizing] = useState(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Left mouse button only; ignore touch/secondary triggers that would fight scroll.
    if (e.button !== 0) return;
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;

    const handleMove = (e: PointerEvent) => {
      setSidebarWidth(clampSidebarWidth(window.innerWidth - e.clientX));
    };
    const stop = () => setIsResizing(false);

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', stop);
    // Lock cursor + suppress text selection while dragging.
    const prevCursor = document.body.style.cursor;
    const prevSelect = document.body.style.userSelect;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', stop);
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevSelect;
    };
  }, [isResizing, setSidebarWidth]);

  return { width: clampSidebarWidth(sidebarWidth), isResizing, onPointerDown };
}
